import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import MobileDropdown from '@/components/MobileDropdown';
import { Plus, X, AlertTriangle, CheckCircle } from 'lucide-react';

export default function Stock() {
  const { axiosAuth, user } = useAuth();
  const [stockData, setStockData] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ingredient_id: '', quantity: '', type: 'purchase', notes: '' });
  const currency = user?.currency || 'USD';

  const load = async () => {
    try {
      const [s, i] = await Promise.all([axiosAuth().get('/stock'), axiosAuth().get('/ingredients')]);
      setStockData(s.data); setIngredients(i.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const ingOptions = ingredients.map(i => ({ value: i.id, label: `${i.name} (${i.unit})` }));
  const typeOptions = [{ value: 'purchase', label: 'Compra (Entrada)' }, { value: 'usage', label: 'Uso (Salida)' }];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ing = ingredients.find(i => i.id === form.ingredient_id);
    try {
      await axiosAuth().post('/stock', { ...form, ingredient_name: ing?.name || '', quantity: parseFloat(form.quantity) || 0 });
      setForm({ ingredient_id: '', quantity: '', type: 'purchase', notes: '' }); setShowForm(false); load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const statusIcon = (status) => {
    if (status === 'critical') return <AlertTriangle size={14} className="text-red-400" />;
    if (status === 'low') return <AlertTriangle size={14} className="text-amber-400" />;
    return <CheckCircle size={14} className="text-emerald-400" />;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="stock-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Inventario</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Gestion de Stock</h2>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="add-stock-btn" className="flex items-center gap-2 px-4 py-2 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors">
          <Plus size={14} /> Movimiento
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="stock-summary">
        <div className="p-6 bg-[#0A0A0A] border border-white/5"><p className="text-2xl font-bold text-red-400">{stockData.filter(s => s.status === 'critical').length}</p><p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Stock Critico</p></div>
        <div className="p-6 bg-[#0A0A0A] border border-white/5"><p className="text-2xl font-bold text-amber-400">{stockData.filter(s => s.status === 'low').length}</p><p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Stock Bajo</p></div>
        <div className="p-6 bg-[#0A0A0A] border border-white/5"><p className="text-2xl font-bold text-emerald-400">{stockData.filter(s => s.status === 'ok').length}</p><p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Stock Normal</p></div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full" data-testid="stock-table">
          <thead><tr className="border-b border-white/10">
            <th className="text-left text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-4">Status</th>
            <th className="text-left text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-4">Ingrediente</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-4">Stock Actual</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-4">Min. Stock</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-2 sm:px-4 hidden sm:table-cell">Dias Restantes</th>
            <th className="text-right text-[10px] uppercase tracking-[0.2em] text-white/40 py-3 px-2 sm:px-4 hidden sm:table-cell">Orden Recomendada</th>
          </tr></thead>
          <tbody>
            {stockData.map((s, i) => (
              <tr key={s.ingredient_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`stock-row-${i}`}>
                <td className="py-3 px-4">{statusIcon(s.status)}</td>
                <td className="py-3 px-4 text-sm font-medium">{s.ingredient_name}</td>
                <td className={`py-3 px-4 text-sm text-right font-mono ${s.status === 'critical' ? 'text-red-400' : s.status === 'low' ? 'text-amber-400' : 'text-white'}`}>{s.current_stock} {s.unit}</td>
                <td className="py-3 px-4 text-sm text-right text-white/40">{s.min_stock} {s.unit}</td>
                <td className="py-3 px-2 sm:px-4 text-sm text-right hidden sm:table-cell">{s.days_remaining > 100 ? 'N/A' : `${s.days_remaining} dias`}</td>
                <td className="py-3 px-2 sm:px-4 text-sm text-right text-gold font-mono hidden sm:table-cell">{s.recommended_order} {s.unit}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {stockData.length === 0 && <p className="text-center text-white/30 py-12 text-sm">Agrega ingredientes para ver el stock</p>}
      </div>

      {showForm && (
        <div data-testid="stock-form-modal">
          <div className="flex items-center gap-3 mb-6">
            <button type="button" onClick={() => setShowForm(false)} className="text-white/40 hover:text-white text-sm flex items-center gap-1"><X size={14} /> Volver</button>
            <h3 className="font-heading text-xl font-bold">Movimiento de Stock</h3>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-lg p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Ingrediente *</label>
                <MobileDropdown options={ingOptions} value={form.ingredient_id} onChange={v => setForm(p => ({ ...p, ingredient_id: v }))} testId="stock-ingredient-select" placeholder="Seleccionar ingrediente..." required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Tipo</label>
                  <MobileDropdown options={typeOptions} value={form.type} onChange={v => setForm(p => ({ ...p, type: v }))} testId="stock-type-select" />
                </div>
                <div><label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Cantidad</label>
                  <input type="number" step="0.01" value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))} required data-testid="stock-quantity-input" className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:border-gold focus:outline-none" /></div>
              </div>
              <div><label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 block">Notas</label>
                <input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} data-testid="stock-notes-input" className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:border-gold focus:outline-none" /></div>
              <button type="submit" data-testid="save-stock-btn" className="w-full py-3 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors mt-4">Registrar Movimiento</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
